import { useState } from 'react';
import { Mail, Send, MapPin, Clock, MessageSquare } from 'lucide-react';
import axios from 'axios';
import CopyableEmail from '@/components/CopyableEmail';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="contact-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 text-center relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-amber-400/[0.03] blur-[120px]" />
        <div className="relative mx-auto max-w-3xl">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-amber-500 mb-4">Get In Touch</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">Contact Us</h1>
          <p className="mt-5 text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
            Have a question about our courses, EAs, or need trading support? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 py-10" data-testid="contact-content">
        <div className="mx-auto max-w-5xl grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl bg-[#0a0f1c] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Mail size={18} className="text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Email</p>
                  <CopyableEmail email="info@1on1trade.com" className="text-sm text-amber-500 hover:text-amber-400 transition-colors" testId="contact-email-copy" />
                </div>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Clock size={18} className="text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Response Time</p>
                  <p className="text-sm text-slate-400">Within 24 hours</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <MessageSquare size={18} className="text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Support</p>
                  <p className="text-sm text-slate-400">Courses, EAs, Indicators & General</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-amber-500/[0.06] to-transparent border border-amber-500/15 p-6">
              <h3 className="text-sm font-bold text-white mb-2">Before You Reach Out</h3>
              <ul className="space-y-2 text-xs text-slate-400">
                <li>- Check our <a href="/indicators" className="text-amber-500 hover:text-amber-400">Learn</a> page for indicator & EA documentation</li>
                <li>- Visit <a href="/reviews" className="text-amber-500 hover:text-amber-400">Reviews</a> to see what other traders say</li>
                <li>- New to forex? Start with <a href="/forex-made-simple" className="text-amber-500 hover:text-amber-400">Forex Made Simple</a></li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="rounded-2xl bg-[#0a0f1c] border border-white/10 p-6 space-y-4" data-testid="contact-form">
              {status === 'success' && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400" data-testid="contact-success">
                  Message sent successfully! We'll get back to you within 24 hours.
                </div>
              )}
              {status === 'error' && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400 flex flex-wrap items-center gap-1" data-testid="contact-error">
                  <span>Something went wrong. Please email us directly at</span>
                  <CopyableEmail email="info@1on1trade.com" className="text-red-300 font-medium" iconSize={12} testId="contact-error-email" />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/30"
                    placeholder="John Smith"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/30"
                    placeholder="you@example.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">Subject</label>
                <select
                  value={form.subject}
                  onChange={e => setForm({...form, subject: e.target.value})}
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:border-amber-500/30"
                  data-testid="contact-subject"
                >
                  <option value="" className="bg-[#0a0f1c]">Select a topic...</option>
                  <option value="courses" className="bg-[#0a0f1c]">Courses & Training</option>
                  <option value="ea" className="bg-[#0a0f1c]">Expert Advisors</option>
                  <option value="indicators" className="bg-[#0a0f1c]">Indicators</option>
                  <option value="billing" className="bg-[#0a0f1c]">Billing & Payments</option>
                  <option value="technical" className="bg-[#0a0f1c]">Technical Support</option>
                  <option value="affiliate" className="bg-[#0a0f1c]">Affiliate Program</option>
                  <option value="other" className="bg-[#0a0f1c]">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/30 resize-none"
                  placeholder="How can we help you?"
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="contact-submit"
              >
                {loading ? 'Sending...' : <><Send size={16} /> Send Message</>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
